import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import ProductSearchSelect from '@/components/ProductSearchSelect';
import { apiFetch } from '@/utils/api';
import { formatCurrency } from '@/utils/formatters';
import {
    PlusIcon,
    TrashIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface Product {
    id: string;
    name: string;
    manufacturer?: string;
    unit_of_measure?: string;
    master_ingredient_id?: string;
}

interface CostCenter {
    id: string;
    name: string;
    address?: string;
}

interface InventoryEntryItem {
    id: string; // Temporary ID for form management
    product_id: string;
    cost_center_id: string;
    quantity: number;
    unit_cost?: number;
    batch_number?: string;
    expiration_date?: string;
    notes?: string;
}

export default function NewInventoryEntryPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const [products, setProducts] = useState<Product[]>([]);
    const [locations, setLocations] = useState<CostCenter[]>([]);
    const [items, setItems] = useState<InventoryEntryItem[]>(() => [
        {
            id: `temp-${Date.now()}-${Math.random()}`,
            product_id: '',
            cost_center_id: '',
            quantity: 0,
            unit_cost: undefined,
            batch_number: '',
            expiration_date: '',
            notes: ''
        }
    ]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [productsResponse, locationsResponse] = await Promise.all([
                apiFetch('/api/products/?limit=100'),
                apiFetch('/api/cost-centers/?is_active=true&limit=100')
            ]);

            if (productsResponse.ok && locationsResponse.ok) {
                const productsData = await productsResponse.json();
                const locationsData = await locationsResponse.json();


                setProducts(productsData.products || []);
                setLocations(locationsData); // cost-centers endpoint returns array directly
            } else {
                throw new Error('Failed to fetch data');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load form data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const addNewItem = () => {
        const newItem: InventoryEntryItem = {
            id: `temp-${Date.now()}-${Math.random()}`,
            product_id: '',
            cost_center_id: '',
            quantity: 0,
            unit_cost: undefined,
            batch_number: '',
            expiration_date: '',
            notes: ''
        };
        setItems(prev => [...prev, newItem]);
    };

    const removeItem = (id: string) => {
        setItems(prev => prev.filter(item => item.id !== id));
    };

    const updateItem = (id: string, field: keyof InventoryEntryItem, value: any) => {
        setItems(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const validateItems = (): string[] => {
        const errors: string[] = [];

        items.forEach((item, index) => {
            if (!item.product_id) {
                errors.push(`Item ${index + 1}: Product is required`);
            }
            if (!item.cost_center_id) {
                errors.push(`Item ${index + 1}: Cost center is required`);
            }
            if (item.quantity <= 0) {
                errors.push(`Item ${index + 1}: Quantity must be greater than 0`);
            }
            if (item.unit_cost !== undefined && item.unit_cost < 0) {
                errors.push(`Item ${index + 1}: Unit cost cannot be negative`);
            }
        });

        return errors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationErrors = validateItems();
        if (validationErrors.length > 0) {
            setError(validationErrors.join('; '));
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            // Filter out empty items and prepare data
            const validItems = items.filter(item =>
                item.product_id && item.cost_center_id && item.quantity > 0
            );

            if (validItems.length === 0) {
                throw new Error('Please add at least one valid item');
            }

            const bulkData = {
                items: validItems.map(item => ({
                    product_id: item.product_id,
                    cost_center_id: item.cost_center_id,
                    quantity: item.quantity,
                    unit_cost: item.unit_cost || null,
                    batch_number: item.batch_number || null,
                    expiration_date: item.expiration_date || null,
                    notes: item.notes || null
                })),
                reference_type: 'manual_entry',
                transaction_date: new Date().toISOString()
            };

            const response = await apiFetch('/api/inventory/bulk-entry/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(bulkData),
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setSuccess(true);
                    setTimeout(() => {
                        navigate({ to: '/inventory'});
                    }, 1000);
                } else {
                    throw new Error(result.errors?.join('; ') || 'Some items failed to process');
                }
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create inventory entries');
            }
        } catch (err) {
            console.error('Error creating inventory entries:', err);
            setError(err instanceof Error ? err.message : 'Failed to create inventory entries');
        } finally {
            setSubmitting(false);
        }
    };

    const getTotalValue = (): number => {
        return items.reduce((total, item) => {
            if (item.unit_cost && item.quantity > 0) {
                return total + (item.unit_cost * item.quantity);
            }
            return total;
        }, 0);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        );
    }

    if (success) {
        return (
            <Layout>
                <div className="max-w-md mx-auto mt-8">
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex">
                            <CheckCircleIcon className="h-5 w-5 text-green-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-800">Success!</h3>
                                <p className="mt-1 text-sm text-green-700">
                                    Inventory entries created successfully. Redirecting to inventory overview...
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate({ to: '..' })}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">New Inventory Entry</h1>
                            <p className="mt-1 text-sm text-gray-500">
                                Add multiple inventory items in bulk
                            </p>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="mt-1 text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {!loading && locations.length === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">No Cost Centers Available</h3>
                                <p className="mt-1 text-sm text-yellow-700">
                                    You need to create cost centers before adding inventory entries. Cost centers represent your different locations (cafes, warehouses, etc.).
                                </p>
                                <div className="mt-3">
                                    <button
                                        onClick={() => navigate({ to: '/cost-centers'})}
                                        className="text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded-md"
                                    >
                                        Manage Cost Centers
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Items */}
                    <div className="bg-white shadow rounded-lg">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-medium text-gray-900">Inventory Items</h3>
                                <button
                                    type="button"
                                    onClick={addNewItem}
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Add Item
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Product
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Cost Center
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Quantity
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Unit Cost
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Batch #
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Expiry Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Notes
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {items.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <ProductSearchSelect
                                                    products={products.map(product => ({
                                                        value: product.id,
                                                        label: product.name
                                                    }))}
                                                    value={item.product_id}
                                                    onChange={(productId: string) => updateItem(item.id, 'product_id', productId)}
                                                    placeholder="Select Product"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <select
                                                    value={item.cost_center_id}
                                                    onChange={(e) => updateItem(item.id, 'cost_center_id', e.target.value)}
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    required
                                                >
                                                    <option value="">Select Cost Center</option>
                                                    {locations.map((location) => (
                                                        <option key={location.id} value={location.id}>
                                                            {location.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="number"
                                                    step="0.001"
                                                    min="0"
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    required
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={item.unit_cost || ''}
                                                    onChange={(e) => updateItem(item.id, 'unit_cost', e.target.value ? parseFloat(e.target.value) : undefined)}
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="0.00"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="text"
                                                    value={item.batch_number || ''}
                                                    onChange={(e) => updateItem(item.id, 'batch_number', e.target.value)}
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="Batch number"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="date"
                                                    value={item.expiration_date || ''}
                                                    onChange={(e) => updateItem(item.id, 'expiration_date', e.target.value)}
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="text"
                                                    value={item.notes || ''}
                                                    onChange={(e) => updateItem(item.id, 'notes', e.target.value)}
                                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    placeholder="Notes"
                                                />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(item.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    disabled={items.length === 1}
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Summary */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">
                                    {items.length} item{items.length !== 1 ? 's' : ''}
                                </span>
                                <span className="text-sm font-medium text-gray-900">
                                    Total Value: {formatCurrency(getTotalValue())}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate({ to: '..' })}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || items.length === 0 || locations.length === 0}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Creating...' : 'Create Entries'}
                        </button>
                    </div>
                </form>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/inventory/new')({
  component: NewInventoryEntryPage,
})
